import { Configuration, LogLevel, PublicClientApplication } from '@azure/msal-browser';

// Shared with app-magic — same Entra app registration so users sign in once.
const TENANT_ID = import.meta.env.VITE_AZURE_TENANT_ID || 'd54794b1-f598-4c0f-a276-6039a39774ac';
const CLIENT_ID = import.meta.env.VITE_AZURE_CLIENT_ID || '1b0c305e-2019-4620-8e30-abadad2171aa';

// Phase 13: exported so AuthGate + useApi can filter cached MSAL accounts
// to the Majans tenant, preventing wrong-account selection when users
// have multiple Microsoft accounts cached in their browser.
export const MAJANS_TENANT_ID = TENANT_ID;

export const msalConfig: Configuration = {
  auth: {
    clientId: CLIENT_ID,
    authority: `https://login.microsoftonline.com/${TENANT_ID}`,
    redirectUri: window.location.origin,
    postLogoutRedirectUri: window.location.origin,
  },
  cache: {
    cacheLocation: 'localStorage',
    // storeAuthStateInCookie was removed in MSAL v5 (default false; not configurable).
  },
  system: {
    loggerOptions: {
      logLevel: LogLevel.Warning,
      loggerCallback: (level, message, containsPii) => {
        if (containsPii) return;
        switch (level) {
          case LogLevel.Error:
            console.error(message);
            break;
          case LogLevel.Warning:
            console.warn(message);
            break;
        }
      },
    },
  },
};

export const loginRequest = {
  scopes: ['User.Read', `api://${CLIENT_ID}/access_as_user`],
};

export const apiScopes = {
  scopes: [`api://${CLIENT_ID}/access_as_user`],
};

export const msalInstance = new PublicClientApplication(msalConfig);

export const AUTH_BYPASS = import.meta.env.VITE_AUTH_BYPASS === 'true';

// Guards so a token-renewal failure bounces the user to interactive sign-in at
// most once, and at most once per minute — prevents any redirect loop.
let tokenRedirectInFlight = false;
const TOKEN_REDIRECT_KEY = 'commercial:tokenRedirectAt';

/**
 * Acquire an API access token for the active (Majans-tenant) account.
 *
 * Tries silent acquisition first. If that fails — overwhelmingly because the
 * hidden-iframe token renewal times out (MSAL `timed_out`) when the browser
 * blocks third-party cookies, so the iframe to login.microsoftonline.com never
 * completes — it falls back to a top-level interactive **redirect**, which is a
 * first-party navigation and isn't subject to that. After the round-trip the
 * cached refresh token serves silent renewals again.
 *
 * Previously useApi only redirected on `InteractionRequiredAuthError`, so a
 * `timed_out` became a dead-end "Authentication failed" error and every authed
 * page (CRM, the calendars, etc.) showed a load/save failure with no recovery.
 *
 * ⚠ Until 2026-10-02 most of those timeouts were this app's own doing: v5 waits
 * for the iframe's answer on a BroadcastChannel and nothing here sent it, so
 * EVERY iframe renewal timed out. main.tsx now does (config/authSignals.ts). Any
 * silent failure still redirects — v5's `timed_out` included — under the same
 * two guards.
 */
export async function acquireApiToken(): Promise<string> {
  const all = msalInstance.getAllAccounts();
  const account =
    msalInstance.getActiveAccount()
    ?? all.find((a) => a.tenantId === MAJANS_TENANT_ID)
    ?? all[0];
  if (!account) throw new Error('Not signed in');

  try {
    const result = await msalInstance.acquireTokenSilent({ ...apiScopes, account });
    return result.accessToken;
  } catch (err) {
    const lastRedirect = Number(sessionStorage.getItem(TOKEN_REDIRECT_KEY) || 0);
    if (!tokenRedirectInFlight && Date.now() - lastRedirect > 60_000) {
      tokenRedirectInFlight = true;
      sessionStorage.setItem(TOKEN_REDIRECT_KEY, String(Date.now()));
      // eslint-disable-next-line no-console
      console.warn('[auth] silent token acquisition failed; redirecting to re-authenticate', err);
      try {
        await msalInstance.acquireTokenRedirect({ ...apiScopes, account });
      } catch (redirectErr) {
        tokenRedirectInFlight = false;
        // eslint-disable-next-line no-console
        console.error('[auth] interactive token redirect failed', redirectErr);
      }
    }
    throw new Error('Re-authenticating…');
  }
}
